import React, { useState, useRef, useEffect } from 'react';
import { Tag } from '../types';

interface MultiSelectDropdownProps {
  availableTags: Tag[];
  selectedTagIds: number[];
  onSelectionChange: (tagIds: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
  showSelectedTags?: boolean;
  buttonStyle?: React.CSSProperties;
  onNewTagCreated?: (tag: Tag) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  availableTags,
  selectedTagIds,
  onSelectionChange,
  placeholder = 'Select tags...',
  disabled = false,
  showSelectedTags = true,
  buttonStyle
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const toggleTag = (tagId: number) => {
    if (disabled) return;

    if (selectedTagIds.includes(tagId)) {
      onSelectionChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onSelectionChange([...selectedTagIds, tagId]);
    }
  };

  const clearAll = () => {
    if (disabled) return;
    onSelectionChange([]);
  };

  const getDisplayText = () => {
    if (selectedTagIds.length === 0) return placeholder;
    if (selectedTagIds.length === 1) {
      const tag = availableTags.find(t => t.id === selectedTagIds[0]);
      return tag?.name || 'Selected';
    }
    return `${selectedTagIds.length} tags selected`;
  };

  const getSelectedTags = () => {
    return availableTags.filter(tag => selectedTagIds.includes(tag.id));
  };

  const filteredTags = availableTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <div ref={dropdownRef} style={{ position: 'relative', zIndex: isOpen ? 1060 : 'auto' }}>
      {/* Main Button */}
      <button
        type="button"
        className={`form-select text-start d-flex justify-content-between align-items-center ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', ...buttonStyle }}
      >
        <span className="text-truncate">{getDisplayText()}</span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="dropdown-menu show border shadow"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            zIndex: 1061,
            maxHeight: '250px',
            overflowY: 'auto',
            marginTop: '2px'
          }}
        >
          {/* Header */}
          <div className="dropdown-header py-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="fw-semibold">Select Tags</small>
              {selectedTagIds.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary px-2 py-1"
                  onClick={clearAll}
                  style={{ fontSize: '0.75rem' }}
                >
                  Clear All
                </button>
              )}
            </div>
            <input
              type="text"
              className="form-control form-control-sm w-100"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{ boxSizing: 'border-box' }}
            />
          </div>

          {/* Options */}
          {filteredTags.length === 0 ? (
            <div className="dropdown-item-text text-muted">
              No tags found
            </div>
          ) : (
            filteredTags.map(tag => (
              <button
                key={tag.id}
                type="button"
                className={`dropdown-item d-flex align-items-center gap-2 ${
                  selectedTagIds.includes(tag.id) ? 'active' : ''
                }`}
                onClick={() => toggleTag(tag.id)}
              >
                <input
                  type="checkbox"
                  className="form-check-input m-0"
                  checked={selectedTagIds.includes(tag.id)}
                  onChange={() => {}} // Handled by parent click
                />
                <span className="badge bg-secondary text-white">{tag.name}</span>
              </button>
            ))
          )}
        </div>
      )}

      {/* Selected Tags Display */}
      {showSelectedTags && selectedTagIds.length > 0 && (
        <div className="mt-2">
          <small className="text-muted">Selected:</small>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {getSelectedTags().map(tag => (
              <span
                key={tag.id}
                className="badge bg-secondary d-inline-flex align-items-center gap-1"
                style={{ fontSize: '0.75rem' }}
              >
                {tag.name}
                {!disabled && (
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ fontSize: '0.5rem' }}
                    onClick={() => toggleTag(tag.id)}
                    aria-label={`Remove ${tag.name}`}
                  />
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
